import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Device } from '../../types/Device';
import { getDeviceByGroup } from '../../api/devices/getDeviceByGroup';
import { getAllDevices } from '../../api/devices/allDevices';
import { useGroupsStore } from '../groups/Groups';

interface DevicesStore {
    devicesByStore: Device[];
    fetchDevicesByStore: (
        groupUID: string,
        SmartDVRToken: string,
        userLogin: string,
        page?: number,
        quantity?: number,
    ) => Promise<Device[]>;
    fetchDevicesByGroupWithSubgroups: (
        groupUID: string,
        SmartDVRToken: string,
        userLogin: string,
    ) => Promise<Device[]>;
    allDevices: Device[];
    fetchAllDevices: (SmartDVRToken: string, userLogin: string) => Promise<Device[]>;
    updateDeviceStatus: (deviceId: string, online: boolean) => void;
}

export const DeviceByGroupStore = create<DevicesStore>()(
    persist(
        (set, get) => ({
            devicesByStore: [],
            allDevices: [],

            fetchDevicesByStore: async (
                groupUID,
                SmartDVRToken,
                userLogin,
                page = 1,
                quantity = 100,
            ) => {
                if (!userLogin || !SmartDVRToken) {
                    console.error('User information is missing.');
                    return get().devicesByStore;
                }

                try {
                    const response = await getDeviceByGroup(
                        SmartDVRToken,
                        userLogin,
                        groupUID,
                        page,
                        quantity,
                    );
                    const devicesByStore = response.data || [];
                    set({ devicesByStore });
                    return devicesByStore;
                } catch (error) {
                    console.error('Error fetching devices by store:', error);
                    return get().devicesByStore;
                }
            },

            fetchDevicesByGroupWithSubgroups: async (
                groupUID,
                SmartDVRToken,
                userLogin,
            ) => {
                if (!userLogin || !SmartDVRToken) {
                    console.error('User information is missing.');
                    return get().devicesByStore;
                }

                try {
                    console.log('Fetching devices for group with subgroups:', groupUID);
                    const response = await getAllDevices(SmartDVRToken, userLogin);
                    console.log('All devices response:', response);

                    let allDevices: Device[] = [];
                    if (Array.isArray(response)) {
                        allDevices = response;
                    } else if (response && response.data && Array.isArray(response.data)) {
                        allDevices = response.data;
                    } else {
                        console.error('Invalid response format from getAllDevices:', response);
                        return get().devicesByStore;
                    }

                    console.log('All devices loaded:', allDevices.length);

                    const findDevicesInGroup = (groupId: string): Device[] => {
                        const devices: Device[] = [];

                        allDevices.forEach((device: Device) => {
                            if (device.groupUID === groupId) {
                                devices.push(device);
                            }
                        });

                        return devices;
                    };

                    const getAllSubgroupIds = (groupId: string, groups: any[]): string[] => {
                        const subgroupIds: string[] = [groupId];

                        const findSubgroups = (parentId: string, groupList: any[]) => {
                            groupList.forEach(group => {
                                if (group.uid === parentId && group.sub_groups && group.sub_groups.length > 0) {
                                    group.sub_groups.forEach((subgroup: any) => {
                                        subgroupIds.push(subgroup.uid);
                                        // Рекурсивно ищем подгруппы в подгруппах
                                        findSubgroups(subgroup.uid, group.sub_groups);
                                    });
                                }
                            });
                        };

                        // Ищем группу в основном списке групп
                        const findGroupInList = (searchId: string, groupList: any[]): any => {
                            for (const group of groupList) {
                                if (group.uid === searchId) {
                                    return group;
                                }
                                if (group.sub_groups && group.sub_groups.length > 0) {
                                    const found = findGroupInList(searchId, group.sub_groups);
                                    if (found) return found;
                                }
                            }
                            return null;
                        };

                        // Начинаем поиск с корневого уровня
                        findSubgroups(groupId, groups);

                        // Если группа не найдена в основном списке, ищем её в подгруппах
                        if (subgroupIds.length === 1) {
                            const foundGroup = findGroupInList(groupId, groups);
                            if (foundGroup && foundGroup.sub_groups && foundGroup.sub_groups.length > 0) {
                                foundGroup.sub_groups.forEach((subgroup: any) => {
                                    subgroupIds.push(subgroup.uid);
                                    findSubgroups(subgroup.uid, foundGroup.sub_groups);
                                });
                            }
                        }

                        return subgroupIds;
                    };

                    const groups = useGroupsStore.getState().groups;
                    console.log('Available groups:', groups.length);
                    console.log('Groups structure:', JSON.stringify(groups, null, 2));

                    if (groups.length === 0) {
                        console.log('Groups not loaded, using fallback method');
                        const response = await getDeviceByGroup(
                            SmartDVRToken,
                            userLogin,
                            groupUID,
                            1,
                            1000,
                        );
                        const devicesByStore = response.data || [];
                        set({ devicesByStore });
                        return devicesByStore;
                    }

                    const allGroupIds = getAllSubgroupIds(groupUID, groups);
                    console.log('Group IDs to search:', allGroupIds);
                    console.log('Searching for devices with groupUID in:', allGroupIds);

                    const devicesInGroupAndSubgroups = allDevices.filter((device: Device) => {
                        const isInGroup = allGroupIds.includes(device.groupUID);
                        if (isInGroup) {
                            console.log(`Device ${device.name} (${device.UID}) found in group ${device.groupUID}`);
                        }
                        return isInGroup;
                    });
                    console.log('Devices found in group and subgroups:', devicesInGroupAndSubgroups.length);

                    set({ devicesByStore: devicesInGroupAndSubgroups });
                    return devicesInGroupAndSubgroups;
                } catch (error) {
                    console.error('Error fetching devices by group with subgroups:', error);
                    return get().devicesByStore;
                }
            },

            fetchAllDevices: async (SmartDVRToken, userLogin) => {
                if (!userLogin || !SmartDVRToken) {
                    console.error('User information is missing.');
                    return get().allDevices;
                }

                try {
                    const response = await getDeviceByGroup(
                        SmartDVRToken,
                        userLogin,
                        '00000000-0000-0000-0000-000000000003', // Все устройства
                        1,
                        1000,
                    );
                    const allDevices = response.data || [];
                    set({ allDevices });
                    return allDevices;
                } catch (error) {
                    console.error('Error fetching all devices:', error);
                    return get().allDevices;
                }
            },

            updateDeviceStatus: (deviceId, online) => {
                set((state) => ({
                    allDevices: state.allDevices.map((device) =>
                        device.UID === deviceId ? { ...device, online } : device,
                    ),
                    devicesByStore: state.devicesByStore.map((device) =>
                        device.UID === deviceId ? { ...device, online } : device,
                    ),
                }));
            },
        }),
        {
            name: 'device-storage',
            partialize: (state) => ({
                allDevices: state.allDevices,
                devicesByStore: state.devicesByStore,
            }),
        },
    ),
);
